import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useChatSessions, useChatMessages, useDeleteChatSession } from "./useTrips";
import type { ChatSessionResponseDto } from "@tabipla/shared";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";

export interface UseChatSessionReturn {
  // State
  currentSessionId: string | null;
  showSessionDropdown: boolean;
  isStartingNewSession: boolean;

  // Data
  sessions: ChatSessionResponseDto[] | undefined;
  chatMessages: ReturnType<typeof useChatMessages>["data"];

  // Actions
  setShowSessionDropdown: (show: boolean) => void;
  handleNewSession: () => void;
  handleSelectSession: (session: ChatSessionResponseDto) => void;
  handleDeleteSession: (sessionId: string, e: React.MouseEvent) => Promise<void>;
  setCurrentSessionId: (id: string | null) => void;
  setIsStartingNewSession: (starting: boolean) => void;

  // Confirm dialog
  dialogProps: ReturnType<typeof useConfirmDialog>["dialogProps"];
}

/**
 * Hook for managing chat session state.
 * Handles session selection, creation, and deletion.
 */
export function useChatSession(tripId: string | undefined): UseChatSessionReturn {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();

  // Session state
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isStartingNewSession, setIsStartingNewSession] = useState(false);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);

  // Data fetching
  const { data: sessions } = useChatSessions(tripId);
  const { data: chatMessages } = useChatMessages(tripId, currentSessionId ?? undefined);
  const deleteSessionMutation = useDeleteChatSession(tripId ?? "");

  // Auto-select most recent session when sessions load
  useEffect(() => {
    if (sessions && sessions.length > 0 && !currentSessionId && !isStartingNewSession) {
      setCurrentSessionId(sessions[0].id);
    }
  }, [sessions, currentSessionId, isStartingNewSession]);

  const handleNewSession = useCallback(() => {
    setCurrentSessionId(null);
    setIsStartingNewSession(true);
    setShowSessionDropdown(false);
  }, []);

  const handleSelectSession = useCallback((session: ChatSessionResponseDto) => {
    setCurrentSessionId(session.id);
    setIsStartingNewSession(false);
    setShowSessionDropdown(false);
  }, []);

  const handleDeleteSession = useCallback(
    async (sessionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const confirmed = await confirm({
        title: t("trips.deleteSession"),
        description: t("trips.deleteSessionConfirm"),
        confirmText: t("common.delete"),
        cancelText: t("common.cancel"),
        destructive: true,
      });
      if (confirmed) {
        await deleteSessionMutation.mutateAsync(sessionId);
        if (currentSessionId === sessionId) {
          setCurrentSessionId(null);
        }
      }
    },
    [confirm, t, deleteSessionMutation, currentSessionId]
  );

  return {
    // State
    currentSessionId,
    showSessionDropdown,
    isStartingNewSession,

    // Data
    sessions,
    chatMessages,

    // Actions
    setShowSessionDropdown,
    handleNewSession,
    handleSelectSession,
    handleDeleteSession,
    setCurrentSessionId,
    setIsStartingNewSession,

    // Confirm dialog
    dialogProps,
  };
}
